const BRACKET_REFERENCE_PATTERN = /\[(@?[A-Z0-9_#]+)\]/gi;

function cloneIr(ir) {
  return JSON.parse(JSON.stringify(ir));
}

function stripCatalogSuffix(variableName) {
  return variableName.replace(/#[A-Z]$/i, '');
}

function normalizeVariableName(variableName) {
  return stripCatalogSuffix(variableName).toUpperCase();
}

function isSystemVariable(variableName) {
  return /^@/i.test(variableName);
}

function getNodeText(node) {
  return node.raw || node.expression || node.text || '';
}

function collectBracketReferences(text) {
  const references = [];
  let match;

  BRACKET_REFERENCE_PATTERN.lastIndex = 0;

  while ((match = BRACKET_REFERENCE_PATTERN.exec(text)) !== null) {
    references.push(match[1]);
  }

  return references;
}

function getUsageKey(usage) {
  return [usage.role || '', usage.line || '', usage.raw || ''].join('|');
}

function addUsage(report, variableName, usage) {
  const normalizedName = normalizeVariableName(variableName);

  if (!report[normalizedName]) {
    report[normalizedName] = {
      name: stripCatalogSuffix(variableName),
      normalizedName,
      classification: isSystemVariable(variableName) ? 'system' : 'unused',
      referencedBy: [],
      usages: [],
    };
  }

  if (usage.referencedBy && !report[normalizedName].referencedBy.includes(usage.referencedBy)) {
    report[normalizedName].referencedBy.push(usage.referencedBy);
  }

  const usageKey = getUsageKey(usage);

  if (report[normalizedName].usages.some((existingUsage) => getUsageKey(existingUsage) === usageKey)) {
    return;
  }

  report[normalizedName].usages.push(usage);
}

function classifyDefinedVariable(entry) {
  const hasComputedUsage = entry.usages.some((usage) => usage.role === 'computed');
  const hasReadUsage = entry.usages.some((usage) => usage.role === 'read');
  const hasDisplayUsage = entry.usages.some((usage) => usage.role === 'display');
  const hasUserInputUsage = entry.usages.some((usage) => usage.role === 'user_input');

  if (hasUserInputUsage && hasReadUsage) {
    return 'read_prefilled';
  }

  if (hasUserInputUsage) {
    return 'user_input';
  }

  if (hasComputedUsage) {
    return 'calculated';
  }

  if (hasReadUsage && hasDisplayUsage) {
    return 'read_display';
  }

  if (hasDisplayUsage) {
    return 'display';
  }

  if (hasReadUsage) {
    return 'read_only';
  }

  return 'unused';
}

function getEntrySource(entry) {
  if (entry.classification === 'read_prefilled') {
    return 'read';
  }

  if (entry.classification === 'read_display') {
    return 'read';
  }

  if (entry.usages.some((usage) => usage.role === 'user_input')) {
    return 'window';
  }

  if (entry.usages.some((usage) => usage.role === 'display')) {
    return 'window';
  }

  if (entry.usages.some((usage) => usage.role === 'read')) {
    return 'read';
  }

  if (entry.usages.some((usage) => usage.role === 'computed')) {
    return 'compute';
  }

  if (entry.usages.some((usage) => usage.role === 'validationReference')) {
    return 'require';
  }

  if (entry.usages.some((usage) => usage.role === 'default')) {
    return 'default';
  }

  if (entry.classification === 'system') {
    return 'system';
  }

  return null;
}

function collectDataTargetRoles(nodes = []) {
  const roles = {};

  nodes.forEach((node) => {
    collectCommandTargets(node).forEach((reference) => {
      const normalizedName = normalizeVariableName(reference);

      roles[normalizedName] = node.type === 'compute' ? 'calculated' : 'read_only';
    });
  });

  return roles;
}

function fieldHasQuotedCaptureLabel(field) {
  if (!field.raw || !field.variable) {
    return false;
  }

  return new RegExp(`"[^"]*"\\s*\\[${field.variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'i').test(field.raw);
}

function isStrongCaptureField(field, targetRoles) {
  return Boolean(field.variable);
}

function collectCommandTargets(node) {
  if (!['read', 'compute'].includes(node.type)) {
    return [];
  }

  if (node.type === 'compute') {
    const targetMatch = getNodeText(node).match(/^\s*COMPUTE\s+\[([A-Z0-9_#]+)\]/i) || (node.expression || '').match(/^\s*\[([A-Z0-9_#]+)\]/i);
    return targetMatch ? [targetMatch[1]] : [];
  }

  if (node.type === 'read' && (node.targets || node.variables || []).length > 0) {
    return node.targets || node.variables;
  }

  const references = collectBracketReferences(getNodeText(node));

  if (node.type === 'read') {
    return references;
  }

  if (references.length > 0) {
    return references;
  }

  const firstToken = (node.expression || '').trim().match(/^([A-Z0-9_#]+)/i);

  return firstToken ? [firstToken[1]] : [];
}

function collectRequireReferences(node) {
  if (node.type !== 'require') {
    return [];
  }

  return collectBracketReferences(getNodeText(node)).filter((reference) => !isSystemVariable(reference));
}

function collectConditionReferences(node) {
  if (node.type !== 'if') {
    return [];
  }

  return collectBracketReferences(getNodeText(node)).filter((reference) => !isSystemVariable(reference));
}

function collectDefaultReferences(node) {
  if (node.type !== 'default') {
    return [];
  }

  const references = [];

  if (node.target) {
    references.push(node.target);
  }

  if (node.valueType === 'variable' && node.value) {
    references.push(node.value);
  }

  return references;
}

function isSelfAssignmentDefault(defaultNode) {
  return (
    defaultNode.defaultType === 'default_assignment' &&
    defaultNode.valueType === 'variable' &&
    normalizeVariableName(defaultNode.target || '') === normalizeVariableName(defaultNode.value || '') &&
    (defaultNode.targetSuffix || null) === (defaultNode.valueSuffix || null)
  );
}

function collectSelfAssignmentDefaults(defaults = []) {
  return defaults.filter((defaultNode) => defaultNode.isSelfAssignment || isSelfAssignmentDefault(defaultNode)).reduce((index, defaultNode) => {
    const normalizedName = normalizeVariableName(defaultNode.target || '');

    if (!normalizedName) {
      return index;
    }

    index[normalizedName] = index[normalizedName] || [];
    index[normalizedName].push(defaultNode);
    return index;
  }, {});
}

function hasSelfAssignmentDefault(field, selfAssignmentDefaults) {
  if (!field?.variable || isSystemVariable(field.variable)) {
    return false;
  }

  const defaults = selfAssignmentDefaults[normalizeVariableName(field.variable)] || [];

  if (defaults.length === 0) {
    return false;
  }

  if (!field.line) {
    return true;
  }

  return defaults.some((defaultNode) => !defaultNode.line || defaultNode.line <= field.line);
}

function applyCatalogToField(field, catalogs) {
  if (!field.variable || isSystemVariable(field.variable)) {
    return field;
  }

  const normalizedVariable = normalizeVariableName(field.variable);
  const matchingCatalog = Object.values(catalogs).find((catalog) => {
    return normalizeVariableName(catalog.variable) === normalizedVariable;
  });

  if (!matchingCatalog) {
    return {
      ...field,
      variableBase: stripCatalogSuffix(field.variable),
      catalogMissing: /#D$/i.test(field.variable),
      options: field.options || [],
    };
  }

  return {
    ...field,
    variableBase: matchingCatalog.variable,
    type: 'select',
    control: 'select',
    catalog: matchingCatalog.id,
    catalogMissing: false,
    options: [...matchingCatalog.options],
  };
}

function applyCatalogNormalization(nextIr) {
  nextIr.forms = nextIr.forms.map((form) => ({
    ...form,
    fields: form.fields.map((field) => {
      if (field.type === 'field_row') {
        return {
          ...field,
          parts: (field.parts || []).map((part) => (part.type === 'field' || part.type === 'select' ? applyCatalogToField(part, nextIr.catalogs) : part)),
        };
      }

      return applyCatalogToField(field, nextIr.catalogs);
    }),
  }));
}

function isWindowEditableClassification(classification) {
  return classification === 'user_input' || classification === 'read_prefilled';
}

function isWindowDisplayClassification(classification) {
  return classification === 'display' || classification === 'read_display';
}

function getFieldClassificationMetadata(entry) {
  if (!entry) {
    return {};
  }

  const editable = isWindowEditableClassification(entry.classification);
  const display = isWindowDisplayClassification(entry.classification);

  return {
    classification: entry.classification,
    source: entry.source,
    editable: display ? false : editable,
    readonly: editable ? false : display ? false : undefined,
    disabled: editable ? false : display ? false : undefined,
    capturable: editable ? true : display ? false : undefined,
  };
}

function createTextTemplate(label, variableName) {
  return label ? `${label}: {${variableName}}` : `{${variableName}}`;
}

function toDisplayField(field) {
  const variableName = field.variableBase || stripCatalogSuffix(field.variable || '');
  const label = (field.label || variableName).replace(/\s*:\s*$/, '');

  return {
    ...field,
    type: 'display',
    control: 'display',
    controlType: 'label',
    label,
    variable: variableName,
    variableBase: variableName,
    name: field.name || variableName,
    binding: field.binding || variableName,
    value: null,
    textTemplate: field.textTemplate || createTextTemplate(label, variableName),
    templateValue: field.templateValue || `{${variableName}}`,
    placeholder: field.placeholder || variableName,
    displayVariable: variableName,
    informational: true,
    editable: false,
    readonly: false,
    disabled: false,
    capturable: false,
    visible: true,
    options: undefined,
  };
}

function applyVariableClassification(nextIr, report, selfAssignmentDefaults) {
  Object.values(nextIr.variables).forEach((variable) => {
    const entry = report[normalizeVariableName(variable.name)];
    variable.classification = entry ? entry.classification : 'unused';
    variable.class = variable.classification;
    variable.source = entry ? entry.source : null;
    variable.referencedBy = entry ? entry.referencedBy : [];
  });

  nextIr.forms = nextIr.forms.map((form) => ({
    ...form,
    fields: form.fields.map((field) => {
      if (field.type === 'field_row') {
        return {
          ...field,
          parts: (field.parts || []).map((part) => {
            if (!part.variable) {
              return part;
            }

            const entry = report[normalizeVariableName(part.variable)];
            const nextPart = {
              ...part,
              variableClass: entry ? entry.classification : isSystemVariable(part.variable) ? 'system' : 'unused',
              referencedBy: entry ? entry.referencedBy : [],
              ...getFieldClassificationMetadata(entry),
            };

            return hasSelfAssignmentDefault(part, selfAssignmentDefaults) ? toDisplayField(nextPart) : nextPart;
          }),
        };
      }

      if (field.type === 'checkbox-group') {
        return {
          ...field,
          options: (field.options || []).map((option) => {
            const entry = report[normalizeVariableName(option.variable)];

            return {
              ...option,
              variableClass: entry ? entry.classification : 'unused',
              referencedBy: entry ? entry.referencedBy : [],
              ...getFieldClassificationMetadata(entry),
            };
          }),
        };
      }

      if (!field.variable) {
        return field;
      }

      const entry = report[normalizeVariableName(field.variable)];
      const nextField = {
        ...field,
        variableClass: entry ? entry.classification : isSystemVariable(field.variable) ? 'system' : 'unused',
        referencedBy: entry ? entry.referencedBy : [],
        ...getFieldClassificationMetadata(entry),
      };

      return hasSelfAssignmentDefault(field, selfAssignmentDefaults) ? toDisplayField(nextField) : nextField;
    }),
  }));
}

function isEditableControl(field) {
  if (field.visible === false || field.capturable === false || field.readonly || field.disabled) {
    return false;
  }

  if (field.type === 'field_row') {
    return (field.parts || []).some((part) => {
      return (
        part.visible !== false &&
        part.capturable !== false &&
        !part.readonly &&
        !part.disabled &&
        isWindowEditableClassification(part.variableClass) &&
        (part.type === 'field' ||
          part.type === 'select' ||
          part.controlType === 'input' ||
          part.control === 'input' ||
          part.controlType === 'checkbox' ||
          part.control === 'checkbox' ||
          part.controlType === 'date' ||
          part.controlType === 'hour' ||
          part.controlType === 'mail' ||
          part.controlType === 'button_menu')
      );
    });
  }

  if (field.type === 'checkbox-group') {
    return (field.options || []).some((option) => isWindowEditableClassification(option.variableClass));
  }

  if (!field.variable || !isWindowEditableClassification(field.variableClass)) {
    return false;
  }

  return (
    field.type === 'field' ||
    field.type === 'select' ||
    field.controlType === 'input' ||
    field.control === 'input' ||
    field.controlType === 'checkbox' ||
    field.control === 'checkbox' ||
    field.controlType === 'date' ||
    field.controlType === 'hour' ||
    field.controlType === 'mail' ||
    field.controlType === 'button_menu'
  );
}

function toReadonlyField(field) {
  const variableName = field.variableBase || stripCatalogSuffix(field.variable || '');

  return {
    ...field,
    type: 'field',
    control: 'input',
    controlType: 'input',
    variable: variableName,
    variableBase: variableName,
    name: field.name || variableName,
    binding: field.binding || variableName,
    value: field.value ?? '',
    templateValue: field.templateValue || `{${variableName}}`,
    placeholder: field.placeholder || variableName,
    displayVariable: variableName,
    informational: true,
    readonly: true,
    disabled: true,
    capturable: false,
    visible: true,
    options: undefined,
  };
}

function fieldRowToReadonlyRow(field) {
  return {
    ...field,
    parts: (field.parts || []).map((part) => {
      if (!part.variable || !['read_only', 'calculated'].includes(part.variableClass)) {
        return part;
      }

      return toReadonlyField(part);
    }),
    informational: true,
    readonly: true,
    disabled: true,
    capturable: false,
    visible: true,
  };
}

function demoteNonInputWindowReferences(nextIr) {
  nextIr.forms = nextIr.forms.map((form) => ({
    ...form,
    fields: form.fields.map((field) => {
      if (field.type === 'field_row') {
        const hasInputPart = (field.parts || []).some((part) => isWindowEditableClassification(part.variableClass));
        const hasReadOnlyPart = (field.parts || []).some((part) => ['read_only', 'calculated'].includes(part.variableClass));

        return !hasInputPart && hasReadOnlyPart ? fieldRowToReadonlyRow(field) : field;
      }

      if (!field.variable || field.type === 'button' || field.type === 'text' || field.type === 'system') {
        return field;
      }

      if (isWindowEditableClassification(field.variableClass)) {
        return field;
      }

      if (['read_only', 'calculated'].includes(field.variableClass)) {
        return toReadonlyField(field);
      }

      return field;
    }),
  }));
}

function getWindowMetrics(form) {
  const controls = form.fields || [];

  return {
    fieldCount: controls.reduce((count, field) => {
      if (field.type !== 'field_row') {
        return isEditableControl(field) ? count + 1 : count;
      }

      return (
        count +
        (field.parts || []).filter((part) => {
          return (
            isWindowEditableClassification(part.variableClass) &&
            (part.type === 'field' ||
              part.type === 'select' ||
              part.controlType === 'input' ||
              part.control === 'input' ||
              part.controlType === 'checkbox' ||
              part.control === 'checkbox')
          );
        }).length
      );
    }, 0),
    messageCount: controls.filter((field) => field.type === 'text' || field.type === 'system' || field.type === 'display' || field.controlType === 'label').length,
    buttonCount: controls.filter((field) => field.type === 'button').length,
  };
}

function classifyWindow(form, metrics = getWindowMetrics(form)) {
  if (metrics.fieldCount > 0) {
    return 'form';
  }

  if (metrics.buttonCount > 0) {
    return 'confirmation';
  }

  return 'validation';
}

function refreshWindowMetrics(nextIr) {
  nextIr.forms = nextIr.forms.map((form) => {
    const metrics = getWindowMetrics(form);

    return {
      ...form,
      ...metrics,
      windowType: classifyWindow(form, metrics),
    };
  });
}

export function analyzeVariables(ast, ir) {
  const nextIr = cloneIr(ir);
  const report = {};
  const targetRoles = collectDataTargetRoles(ast.nodes);
  const selfAssignmentDefaults = collectSelfAssignmentDefaults(nextIr.defaults || []);

  Object.values(nextIr.variables).forEach((variable) => {
    const normalizedName = normalizeVariableName(variable.name);
    report[normalizedName] = {
      name: variable.name,
      normalizedName,
      definedAt: variable.line,
      datatype: variable.datatype,
      length: variable.length,
      classification: 'unused',
      referencedBy: [],
      usages: [],
    };
  });

  nextIr.forms.forEach((form) => {
    form.fields.forEach((field) => {
      if (field.type === 'field_row') {
        (field.parts || []).forEach((part) => {
          if (!part.variable || isSystemVariable(part.variable) || !['field', 'select'].includes(part.type)) {
            return;
          }

          if (!isStrongCaptureField(part, targetRoles)) {
            return;
          }

          if (hasSelfAssignmentDefault(part, selfAssignmentDefaults)) {
            addUsage(report, part.variable, {
              role: 'display',
              source: 'window',
              referencedBy: 'window',
              line: part.line || field.line,
              formId: form.id,
              raw: part.raw || field.raw,
            });
            return;
          }

          addUsage(report, part.variable, {
            role: 'user_input',
            line: part.line || field.line,
            formId: form.id,
            raw: part.raw || field.raw,
          });
        });
        return;
      }

      if (field.type === 'checkbox-group') {
        (field.options || []).forEach((option) => {
          addUsage(report, option.variable, {
            role: 'user_input',
            line: option.line || field.line,
            formId: form.id,
            raw: option.raw || field.raw,
          });
        });
        return;
      }

      if (!field.variable || isSystemVariable(field.variable) || !['field', 'select'].includes(field.type)) {
        return;
      }

      if (!isStrongCaptureField(field, targetRoles)) {
        return;
      }

      if (hasSelfAssignmentDefault(field, selfAssignmentDefaults)) {
        addUsage(report, field.variable, {
          role: 'display',
          source: 'window',
          referencedBy: 'window',
          line: field.line,
          formId: form.id,
          raw: field.raw,
        });
        return;
      }

      addUsage(report, field.variable, {
        role: 'user_input',
        line: field.line,
        formId: form.id,
        raw: field.raw,
      });
    });
  });

  ast.nodes.forEach((node) => {
    collectBracketReferences(getNodeText(node)).forEach((reference) => {
      if (!isSystemVariable(reference)) {
        return;
      }

      addUsage(report, reference, {
        role: 'system',
        line: node.line,
        nodeType: node.type,
        raw: getNodeText(node),
      });
    });

    collectCommandTargets(node).forEach((reference) => {
      addUsage(report, reference, {
        role: node.type === 'compute' ? 'computed' : 'read',
        referencedBy: node.type,
        line: node.line,
        nodeType: node.type,
        raw: getNodeText(node),
      });
    });

    collectDefaultReferences(node).forEach((reference) => {
      addUsage(report, reference, {
        role: 'default',
        referencedBy: 'default',
        line: node.line,
        nodeType: node.type,
        raw: getNodeText(node),
      });
    });

    collectRequireReferences(node).forEach((reference) => {
      addUsage(report, reference, {
        type: 'validationReference',
        role: 'validationReference',
        referencedBy: 'require',
        line: node.line,
        nodeType: node.type,
        raw: getNodeText(node),
      });
    });

    collectConditionReferences(node).forEach((reference) => {
      addUsage(report, reference, {
        role: 'condition',
        referencedBy: 'if',
        line: node.line,
        nodeType: node.type,
        raw: getNodeText(node),
      });
    });
  });

  Object.values(report).forEach((entry) => {
    if (entry.classification === 'system') {
      entry.class = entry.classification;
      entry.source = getEntrySource(entry);
      return;
    }

    entry.classification = classifyDefinedVariable(entry);
    entry.class = entry.classification;
    entry.source = getEntrySource(entry);
  });

  applyCatalogNormalization(nextIr);
  applyVariableClassification(nextIr, report, selfAssignmentDefaults);
  demoteNonInputWindowReferences(nextIr);
  refreshWindowMetrics(nextIr);

  nextIr.variableReport = {
    variables: Object.values(report).sort((left, right) => {
      if (left.classification !== right.classification) {
        return left.classification.localeCompare(right.classification);
      }

      return left.name.localeCompare(right.name);
    }),
  };

  return nextIr;
}
